/**
 * Choosing, not wording, for credit control wave 1 (docs/features/credit-control/
 * 06_ui_strategy.md): which chip variant a customer's credit position gets,
 * how a list row's status reads, and the button each hold decision carries.
 *
 * Headless: no styling, no DOM. What a terms label, stop reason or hold outcome
 * says is the backend's (it arrives as a label on the response); this only
 * picks between states and assembles a tooltip from labels already given.
 */

import type { AgedDebtBucketKey } from "../data/aged-debt-types";
import { ApiError } from "../data/api/errors";
import type {
  BillingAccountListItem,
  CustomerCreditView,
  HoldDecision,
} from "../data/credit-control-types";
import { extractFieldErrors } from "../data/validation";
import { formatShortDateOrDash, formatTimeOrDash } from "./formatters";

export type CreditChipVariant = "on_stop" | "neutral";

export interface CreditChip {
  variant: CreditChipVariant;
  label: string;
  /** What hovering explains: why it is on stop, the other methods, or who a sub-account is billed to. */
  tooltip: string | null;
}

/**
 * The customer page header's chip (06 §2): amber "On stop" when the account is
 * stopped; "Credit · {terms}" when credit account is on (the other methods in
 * the tooltip); otherwise the methods the payer has ("Card only" by default,
 * "Card · Pro-forma", "Pro-forma only"). A sub-account shows its payer's and
 * says whom it is billed to. Pollards' internal account is just "Internal",
 * whatever its methods: it is never billed, so none of that applies.
 */
export function creditChip(view: CustomerCreditView): CreditChip {
  const account = view.billingAccount;
  const billedTo = view.payer ? `Billed to ${view.payer.customerName}` : null;
  const join = (parts: (string | null)[]) =>
    parts.filter(Boolean).join(" · ") || null;
  if (account?.kind === "internal") {
    return {
      variant: "neutral",
      label: account.kindShortLabel,
      tooltip: account.kindLabel,
    };
  }
  if (account?.onStop) {
    const stop = account.stop;
    return {
      variant: "on_stop",
      label: "On stop",
      tooltip: join([
        stop
          ? join([
              stop.reasonLabel,
              `since ${formatShortDateOrDash(stop.at)}`,
              stop.byName,
            ])
          : null,
        billedTo,
      ]),
    };
  }
  if (account?.terms) {
    return {
      variant: "neutral",
      label: `Credit · ${account.terms.displayLabel}`,
      tooltip: join([
        account.otherMethodsLabel ? `Also: ${account.otherMethodsLabel}` : null,
        billedTo,
      ]),
    };
  }
  return { variant: "neutral", label: view.methodsLabel, tooltip: billedTo };
}

/**
 * The list's Status column: "Internal" for Pollards' own stock account, else
 * "On stop" in amber, else the PO rule's note ("PO to follow") when one is
 * needed, else nothing.
 */
export function creditListStatus(
  item: Pick<
    BillingAccountListItem,
    "kind" | "kindShortLabel" | "onStop" | "poRuleNote"
  >,
): { label: string; variant: CreditChipVariant } | null {
  if (item.kind === "internal")
    return { label: item.kindShortLabel, variant: "neutral" };
  if (item.onStop) return { label: "On stop", variant: "on_stop" };
  if (item.poRuleNote) return { label: item.poRuleNote, variant: "neutral" };
  return null;
}

/** The fixed buttons on a pending hold; "released_without_credit" is labelled by the payer's other methods. */
const HOLD_DECISION_BUTTON_LABELS: Record<
  Exclude<HoldDecision, "released_without_credit">,
  string
> = {
  released: "Release",
  release_to_proof_only: "Proof only",
  refused: "Refuse",
};

/**
 * The button for a hold decision, or null when there is none to offer:
 * releasing without credit is named for the payer's other methods ("Card
 * only", "Pro-forma only", "Card or pro-forma") and hidden when the payer has
 * only credit account (`otherMethodsLabel` null).
 */
export function holdDecisionButtonLabel(
  decision: HoldDecision,
  otherMethodsLabel: string | null,
): string | null {
  return decision === "released_without_credit"
    ? otherMethodsLabel
    : HOLD_DECISION_BUTTON_LABELS[decision];
}

/** Order the buttons read in: the most permissive first. */
export const HOLD_DECISION_ORDER: readonly HoldDecision[] = [
  "released",
  "release_to_proof_only",
  "released_without_credit",
  "refused",
];

/** "14 Oct 10:02": the day and time a hold arrived or was decided. */
export function holdMoment(iso: string): string {
  return `${formatShortDateOrDash(iso)} ${formatTimeOrDash(iso)}`;
}

/**
 * A page a response names (a hold's `subjectUrl`), as a link target: only a
 * path on this app. Anything else (`https://…`, `//host`) is not linked.
 */
export function appPathOrNull(url: string | null): string | null {
  if (url === null) return null;
  return url.startsWith("/") && !url.startsWith("//") && !url.startsWith("/\\")
    ? url
    : null;
}

/** A recorded credit limit in pounds (15000 for £15,000); null when none is set. */
export function creditLimitPounds(pence: number | null): number | null {
  return pence === null ? null : Math.round(pence) / 100;
}

const limitDigits = (text: string): string => text.replace(/[£,\s]/g, "");

/** The pounds typed in the limit field, in pence; blank is no limit. */
export function creditLimitPenceFromInput(text: string): number | null {
  const cleaned = limitDigits(text);
  if (cleaned === "") return null;
  const pounds = Number(cleaned);
  if (!Number.isFinite(pounds) || pounds < 0) return null;
  return Math.round(pounds * 100);
}

/**
 * The rule a credit account's limit lives under, in the words the backend refuses with (credit control's
 * CREDIT_LIMIT_RULE_SENTENCE): no limit, no credit account (Ben, d75). The save is refused inline with it, and the
 * server's refusal says the same thing.
 */
export const CREDIT_LIMIT_RULE_SENTENCE =
  "A credit account needs a credit limit above zero. Without one the customer pays by card or pro-forma only.";

/** True when the typed limit is not blank and not an amount above zero (blank is "missing", a different message). */
export function isInvalidCreditLimitInput(text: string): boolean {
  const cleaned = limitDigits(text);
  if (cleaned === "") return false;
  const pounds = Number(cleaned);
  return !Number.isFinite(pounds) || pounds <= 0;
}

/**
 * The hint beside a customer PO number field: "Like 4500000000 (45 then 8
 * digits)". Both parts are credit control's (the read carries them, null when
 * the payer's PO rule needs no PO or no format is set); null when there is
 * nothing to show.
 */
export function poFormatHint(format: {
  po_format_label?: string | null;
  po_format_example?: string | null;
}): string | null {
  const { po_format_label: label, po_format_example: example } = format;
  if (!example) return null;
  return label ? `Like ${example} (${label})` : `Like ${example}`;
}

// ── Aged debt and disputes (L spec §4.7) ──────────────────────────────────

/** The five ageing buckets in column order, with their column headings (standard credit-control ageing). */
export const AGED_DEBT_BUCKETS: readonly {
  column: AgedDebtBucketKey;
  label: string;
}[] = [
  { column: "not_yet_due_pence", label: "Not yet due" },
  { column: "days_1_to_30_pence", label: "1–30" },
  { column: "days_31_to_60_pence", label: "31–60" },
  { column: "days_61_to_90_pence", label: "61–90" },
  { column: "over_90_pence", label: "Over 90" },
];

/** A payer's oldest overdue invoice: "112 days", "1 day", or a dash while nothing is overdue. */
export function oldestOverdueLabel(days: number | null): string {
  if (days === null) return "–";
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/** An invoice's days late as a table cell: the count once overdue, "not due" before. */
export function daysLateLabel(daysOverdue: number): string {
  return daysOverdue > 0 ? String(daysOverdue) : "not due";
}

/**
 * How a refused Record or Resolve dispute reads in its dialog: the server's message for the one input (`reason` or
 * `note`) goes under that input; anything else it said (a 409 such as "already disputed", a step no longer offered)
 * is the banner, verbatim.
 */
export interface DisputeFailure {
  fieldMessage: string | null;
  banner: string | null;
}

export function disputeFailure(
  err: unknown,
  field: "reason" | "note",
): DisputeFailure {
  const fieldErrors = extractFieldErrors(err);
  const own = fieldErrors.filter((e) => e.field === field);
  const others = fieldErrors.filter((e) => e.field !== field);
  if (own.length > 0 || others.length > 0) {
    return {
      fieldMessage: own[0]?.message ?? null,
      banner: others.length > 0 ? others.map((e) => e.message).join(" ") : null,
    };
  }
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    const message =
      detail && typeof detail === "object"
        ? (detail as { message?: unknown }).message
        : detail;
    return {
      fieldMessage: null,
      banner: typeof message === "string" ? message : err.detail || err.message,
    };
  }
  return {
    fieldMessage: null,
    banner: err instanceof Error ? err.message : "Could not record that.",
  };
}
